import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Typography, alpha } from "@mui/material";
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as ResetIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
} from "@mui/icons-material";
import { getDocument, PDFDocumentProxy } from "pdfjs-dist";
import { useAppTheme } from "@/hooks/useAppTheme";
import "@/lib/pdfWorkerSetup";

// =============================================================================
// Types
// =============================================================================

export interface PageDimensions {
  /** Canvas pixel width at the current scale */
  width: number;
  /** Canvas pixel height at the current scale */
  height: number;
  /** Current zoom scale */
  scale: number;
}

export interface PdfViewerProps {
  /** PDF data — accepts Blob (from useDownloadDocument) or Uint8Array (from usePdfSourcePreview) */
  source: Blob | Uint8Array | null;
  /** Page to scroll into view (all-pages mode) or navigate to (single-page mode). 1-indexed. */
  focusPage?: number | null;
  /** Initial zoom scale (default 1.5) */
  defaultScale?: number;
  /** Show zoom toolbar (default true) */
  showToolbar?: boolean;
  /** Render one page at a time with prev/next navigation (default false — renders all pages) */
  singlePageMode?: boolean;
  /** External loading state — shown while data is being fetched */
  isLoading?: boolean;
  /** External error message */
  error?: string | null;
  /** Called when the PDF document finishes loading */
  onDocumentLoad?: (pageCount: number) => void;
  /** Render prop for per-page overlays (used by PdfViewerWithHighlights for bounding boxes) */
  renderPageOverlay?: (pageNumber: number, dimensions: PageDimensions) => ReactNode;
}

// =============================================================================
// Constants
// =============================================================================

const DEFAULT_SCALE = 1.5;
const MIN_SCALE = 0.5;
const MAX_SCALE = 4.0;
const SCALE_STEP = 0.25;

// =============================================================================
// Component
// =============================================================================

export const PdfViewer = ({
  source,
  focusPage = null,
  defaultScale = DEFAULT_SCALE,
  showToolbar = true,
  singlePageMode = false,
  isLoading = false,
  error = null,
  onDocumentLoad,
  renderPageOverlay,
}: PdfViewerProps) => {
  const { theme, isDarkMode } = useAppTheme();
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [scale, setScale] = useState(defaultScale);
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const pageContainerRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const pdfDocRef = useRef<PDFDocumentProxy | null>(null);

  const numPages = pdfDoc?.numPages ?? 0;

  // ───────────────────────────────────────────────────────────────
  // Sync currentPage with focusPage prop
  // ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (focusPage !== null && focusPage >= 1 && focusPage <= numPages) {
      setCurrentPage(focusPage);
    }
  }, [focusPage, numPages]);

  // ───────────────────────────────────────────────────────────────
  // Convert Blob → Uint8Array (pass-through if already Uint8Array)
  // ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!source) {
      setPdfBytes(null);
      return;
    }

    if (source instanceof Blob) {
      let cancelled = false;
      void source.arrayBuffer().then((buf) => {
        if (!cancelled) setPdfBytes(new Uint8Array(buf));
      });
      return () => {
        cancelled = true;
      };
    }

    // Already Uint8Array
    setPdfBytes(source);
  }, [source]);

  // ───────────────────────────────────────────────────────────────
  // Load PDF document from bytes
  // ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!pdfBytes) {
      // Destroy the previous document if source is cleared
      if (pdfDocRef.current) {
        void pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
      setPdfDoc(null);
      return;
    }

    let cancelled = false;

    // Copy the data since pdf.js transfers the underlying ArrayBuffer
    const loadingTask = getDocument({ data: new Uint8Array(pdfBytes) });

    const loadPdf = async () => {
      try {
        setLoadError(null);
        const doc = await loadingTask.promise;
        if (!cancelled) {
          // Destroy the previous document before replacing it
          if (pdfDocRef.current) {
            void pdfDocRef.current.destroy();
          }
          pdfDocRef.current = doc;
          setPdfDoc(doc);
          setCurrentPage(focusPage && focusPage >= 1 ? focusPage : 1);
          onDocumentLoad?.(doc.numPages);
        } else {
          // Effect was cleaned up while loading — destroy the new doc we won't use
          void doc.destroy();
        }
      } catch (err) {
        if (!cancelled) {
          console.error("[PdfViewer] Failed to load PDF:", err);
          setLoadError("Failed to render PDF document.");
        }
      }
    };

    void loadPdf();

    return () => {
      cancelled = true;
      // Cancel the in-flight loading task and its web worker
      void loadingTask.destroy();
    };
  }, [pdfBytes, onDocumentLoad]);

  // ───────────────────────────────────────────────────────────────
  // Destroy the PDF document on unmount
  // ───────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (pdfDocRef.current) {
        void pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
    };
  }, []);

  // ───────────────────────────────────────────────────────────────
  // Render a single PDF page to canvas
  // ───────────────────────────────────────────────────────────────
  const renderPage = useCallback(
    async (pageNumber: number, currentScale: number) => {
      if (!pdfDoc) return;

      try {
        const page = await pdfDoc.getPage(pageNumber);
        const canvas = canvasRefs.current.get(pageNumber);
        if (!canvas) return;

        const context = canvas.getContext("2d");
        if (!context) return;

        const viewport = page.getViewport({ scale: currentScale });
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({ canvasContext: context, viewport, canvas }).promise;
      } catch (err) {
        console.error(`[PdfViewer] Error rendering page ${pageNumber}:`, err);
      }
    },
    [pdfDoc]
  );

  // ───────────────────────────────────────────────────────────────
  // Render pages when doc loads or scale changes
  // ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!pdfDoc) return;

    if (singlePageMode) {
      void renderPage(currentPage, scale);
    } else {
      const renderAllPages = async () => {
        for (let i = 1; i <= pdfDoc.numPages; i++) {
          await renderPage(i, scale);
        }
      };
      void renderAllPages();
    }
  }, [pdfDoc, scale, renderPage, singlePageMode, currentPage]);

  // ───────────────────────────────────────────────────────────────
  // Scroll to focused page (all-pages mode only)
  // ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (singlePageMode || focusPage === null || !pdfDoc) return;

    const container = pageContainerRefs.current.get(focusPage);
    if (container) {
      const timer = setTimeout(() => {
        container.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [focusPage, pdfDoc, singlePageMode]);

  // ───────────────────────────────────────────────────────────────
  // Zoom handlers
  // ───────────────────────────────────────────────────────────────
  const handleZoomIn = () => setScale((s) => Math.min(s + SCALE_STEP, MAX_SCALE));
  const handleZoomOut = () => setScale((s) => Math.max(s - SCALE_STEP, MIN_SCALE));
  const handleZoomReset = () => setScale(defaultScale);

  // ───────────────────────────────────────────────────────────────
  // Page navigation (single-page mode)
  // ───────────────────────────────────────────────────────────────
  const handlePrevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const handleNextPage = () => setCurrentPage((p) => Math.min(p + 1, numPages));

  // ───────────────────────────────────────────────────────────────
  // Loading / error states
  // ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  const displayError = error || loadError;
  if (displayError) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={2}>
        <Typography variant="body2" color="error.main">
          {displayError}
        </Typography>
      </Box>
    );
  }

  if (!pdfDoc) return null;

  // Visual zoom ratio relative to the default "fit-to-width" scale.
  const zoomRatio = scale / defaultScale;

  // ───────────────────────────────────────────────────────────────
  // Determine which pages to render
  // ───────────────────────────────────────────────────────────────
  const pagesToRender = singlePageMode ? [currentPage] : Array.from({ length: numPages }, (_, i) => i + 1);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, height: "100%" }}>
      {/* Toolbar: zoom controls + page navigation */}
      {showToolbar && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            position: "sticky",
            top: 0,
            zIndex: 1,
            py: 0.5,
            bgcolor: alpha(isDarkMode ? theme.palette.grey[900] : theme.palette.background.paper, 0.95),
            backdropFilter: "blur(4px)",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {/* Page navigation (single-page mode) */}
          {singlePageMode && (
            <>
              <IconButton size="small" onClick={handlePrevPage} disabled={currentPage <= 1} title="Previous page">
                <PrevIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="caption"
                sx={{ minWidth: 64, textAlign: "center", fontWeight: 500, userSelect: "none" }}
              >
                {currentPage} / {numPages}
              </Typography>
              <IconButton size="small" onClick={handleNextPage} disabled={currentPage >= numPages} title="Next page">
                <NextIcon fontSize="small" />
              </IconButton>
              <Box sx={{ mx: 0.5, borderLeft: 1, borderColor: "divider", height: 20 }} />
            </>
          )}

          {/* Zoom controls */}
          <IconButton size="small" onClick={handleZoomOut} disabled={scale <= MIN_SCALE} title="Zoom out">
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ minWidth: 48, textAlign: "center", fontWeight: 500, userSelect: "none" }}>
            {Math.round(scale * 100)}%
          </Typography>
          <IconButton size="small" onClick={handleZoomIn} disabled={scale >= MAX_SCALE} title="Zoom in">
            <ZoomInIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleZoomReset}
            disabled={scale === defaultScale}
            title="Reset zoom"
            sx={{ ml: 0.5 }}
          >
            <ResetIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* PDF pages */}
      {pagesToRender.map((pageNum) => {
        const isFocusPage = focusPage === pageNum;
        const canvas = canvasRefs.current.get(pageNum);
        const dimensions: PageDimensions | null = canvas ? { width: canvas.width, height: canvas.height, scale } : null;

        return (
          <Box
            key={`page-${pageNum}`}
            ref={(el: HTMLDivElement | null) => {
              if (el) pageContainerRefs.current.set(pageNum, el);
            }}
            sx={{
              position: "relative",
              border: 2,
              borderColor: isFocusPage ? theme.palette.primary.main : "divider",
              borderRadius: 1,
              // flexShrink: 0 keeps each page at its natural rendered
              // height inside the parent flex column. Without it, default
              // flex-shrink:1 squeezes all-pages-mode into per-page mini
              // scroll thumbnails (each ~250px tall) when the column's
              // total natural height exceeds the drawer height. Vertical
              // scrolling is handled by an ancestor with overflow:auto;
              // each page just sits at full size.
              flexShrink: 0,
              // overflowX is enough to handle horizontal scroll when the
              // canvas is wider than the column at high zoom levels.
              // overflowY:visible lets the page contribute its natural
              // height to the column instead of forcing internal scroll.
              overflowX: "auto",
              overflowY: "visible",
              boxShadow: isFocusPage
                ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.35)}, 0 0 16px ${alpha(theme.palette.primary.main, 0.2)}`
                : `0 1px 3px ${alpha(theme.palette.common.black, 0.08)}`,
              transition: "border-color 0.2s, box-shadow 0.2s",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: `${zoomRatio * 100}%`,
                minWidth: "100%",
              }}
            >
              <canvas
                ref={(el) => {
                  if (el) canvasRefs.current.set(pageNum, el);
                }}
                // height: "auto" preserves the page's natural aspect ratio
                // when CSS width is constrained by the container. Without
                // it, the canvas renders at its intrinsic pixel height
                // (set programmatically to viewport.height) regardless of
                // the CSS-scaled width, which squishes multi-page PDFs
                // vertically when stacked in the side drawer.
                style={{ display: "block", width: "100%", height: "auto" }}
              />

              {/* Per-page overlay slot (used for highlights) */}
              {renderPageOverlay && dimensions && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                  }}
                >
                  {renderPageOverlay(pageNum, dimensions)}
                </Box>
              )}
            </Box>

            {/* Page number indicator (all-pages mode only) */}
            {!singlePageMode && (
              <Box
                sx={{
                  position: "sticky",
                  bottom: 4,
                  float: "right",
                  mr: 0.5,
                  mt: -3.5,
                  bgcolor: isFocusPage
                    ? alpha(theme.palette.primary.main, 0.9)
                    : alpha(isDarkMode ? theme.palette.grey[800] : theme.palette.common.white, 0.85),
                  color: isFocusPage ? theme.palette.primary.contrastText : "text.secondary",
                  fontWeight: isFocusPage ? 600 : 400,
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontSize: "0.75rem",
                }}
              >
                Page {pageNum}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
